import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';

export type ActionRowTone = 'monitor' | 'danger';

interface ActionRowProps {
    icon: ComponentType<{ className?: string }>;
    label: string;
    description: string;
    tone?: ActionRowTone;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
}

/**
 * Full-width action row: icon + label + description + chevron. Ported from
 * files-viewer's ManageMonitoringLanding ActionRow to keep the doctor and
 * admin "manage monitoring" surfaces visually aligned.
 */
function ActionRow({
    icon: Icon,
    label,
    description,
    tone = 'monitor',
    onClick,
    disabled = false,
    loading = false
}: ActionRowProps) {
    const isDanger = tone === 'danger';
    const isDisabled = disabled || loading;

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border text-left transition-colors disabled:opacity-50 w-full ${
                isDanger
                    ? 'border-red-200 bg-red-50 hover:bg-red-100'
                    : 'border-gray-200 hover:bg-gray-50'
            }`}
        >
            <div
                className={`w-5 h-5 flex-shrink-0 ${isDanger ? 'text-red-500' : 'text-[#007E87]'}`}
            >
                {loading ? (
                    <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                    <Icon className="w-5 h-5" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div
                    className={`text-sm font-semibold ${isDanger ? 'text-red-600' : 'text-gray-800'}`}
                >
                    {label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{description}</div>
            </div>
            <ChevronRightIcon
                className={`w-4 h-4 flex-shrink-0 ${isDanger ? 'text-red-300' : 'text-gray-300'}`}
            />
        </button>
    );
}

export { ActionRow };
